import { View } from "react-native";
import React, { useState } from "react";
import MainContainer from "components/MainContainer";
import { UpdateBillingIntervalScreenProps } from "navigation/types";
import { Text } from "@ui-kitten/components";
import toast from "utils/toast";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import ConfirmPurchaseButton from "components/ConfirmPurchaseButton";
import Spacer from "components/Spacer";
import ConfirmPurchaseDialog from "./ConfirmPurchaseDialog";
import {
  changeBillingIntervalFetcher,
  useFetchRemainingChargesQuery,
} from "queries/account.queries";
import { useAccount } from "hooks/useAccount";
import { useMutation } from "@tanstack/react-query";
import i18n from "plugins/localization";
import { formatCurrency } from "utils/appUtils";

const UpdateBilling = ({
  navigation,
  route,
}: UpdateBillingIntervalScreenProps) => {
  const [dialog, setDialog] = useState(false);

  const { pid: planPackageId, mid: memberId } = route.params;
  const { account, fetchAccount } = useAccount();
  const { data: remainingCharge } = useFetchRemainingChargesQuery({
    accountId: account?.id,
    planPackageId,
  });
  const { mutateAsync: changeBillingInterval, isLoading: loading } =
    useMutation(changeBillingIntervalFetcher);
  if (!planPackageId) {
    setTimeout(() => {
      goBack();
    }, 150);
  }

  const goBack = () =>
    navigation.replace("ManageSubscriptions", {
      screen: "SubscriptionDetail",
      params: { memberId },
    });

  const handleUpdateInterval = async () => {
    await changeBillingInterval({
      accountId: account!.id,
      planPackageId: parseInt(planPackageId),
    });
    fetchAccount();
    goBack();
    toast.success(i18n.t("screens.UpdateBilling.billingIntervalUpdated"));
  };

  if (!remainingCharge) return null;

  const name = remainingCharge.remainingCharges[0]?.planPackageDetails.name;
  const totalQuantity = remainingCharge.remainingCharges.length;

  return (
    <MainContainer
      title={i18n.t("screens.UpdateBilling.updateBillingInterval")}
      goBackAction={goBack}
      grayBackground
    >
      <View style={{ padding: 15 }}>
        <Text category="h6">
          {i18n.t("screens.UpdateBilling.changePaymentIntervalToFull")}
        </Text>

        <Spacer />

        <BaseCard title={name}>
          <BaseCardListItem
            title={i18n.t("common.totalQuantity", { quantity: totalQuantity })}
            showArrow={false}
          />
        </BaseCard>

        <Spacer />

        <BaseCard title={i18n.t("screens.UpdateBilling.amountDueToday")}>
          <BaseCardListItem
            title={
              <Text>
                {formatCurrency(remainingCharge?.totalRemainingCharge)}
              </Text>
            }
            showArrow={false}
          />
        </BaseCard>
        <Spacer />

        {!!account?.billingLast4 && <PaymentMethodCard />}

        <Spacer />

        <ConfirmPurchaseButton
          charge={parseFloat(remainingCharge?.totalRemainingCharge)}
          loading={loading}
          onPress={() => setDialog(true)}
          buttonText={i18n
            .t("screens.UpdateBilling.updateBillingInterval")
            .toLocaleUpperCase()}
        ></ConfirmPurchaseButton>

        <ConfirmPurchaseDialog
          visible={dialog}
          setVisible={setDialog}
          amountDue={formatCurrency(remainingCharge?.totalRemainingCharge)}
          cancelText={i18n.t("common.cancel")}
          okText="AGREE & PURCHASE"
          okBtnProps={{ status: "primary", appearance: "filled" }}
          loading={loading}
          onOk={handleUpdateInterval}
        />
      </View>
    </MainContainer>
  );
};

export default UpdateBilling;
